import { Badge } from "../../core/Badge.js";
import { cn } from "../../foundations/cn.js";
import type { LiveBreakBadgeStatus } from "./liveBreakState.js";

const TONE = {
  live: "neutral",
  next: "success",
  ending: "neutral",
  closed: "neutral",
} as const satisfies Record<LiveBreakBadgeStatus, string>;

/**
 * LIVE and ENDING sit on the neutral fill with the word in the primary text, so it is never small
 * text over a color; the hairline ring keeps the pill's shape on a card whose fill the neutral one
 * matches. CLOSED is the quiet one: the secondary text, outlined by the stronger edge.
 */
const PILL = {
  live: "text-text-primary ring-1 ring-border-subtle ring-inset",
  next: "",
  ending: "text-text-primary ring-1 ring-border-subtle ring-inset",
  closed: "ring-1 ring-border-default ring-inset",
} as const satisfies Record<LiveBreakBadgeStatus, string>;

/** The status dot: the store's live red, as the ticker and the widget draw it, or ENDING's amber. */
const DOT = {
  live: "bg-status-danger motion-safe:animate-pulse",
  ending: "bg-status-warning",
} as const;

export interface LiveBreakStatusBadgeProps {
  /** LIVE while it runs (or the entry on stage), NEXT for Up Next, ENDING while it Drains, CLOSED. */
  status: LiveBreakBadgeStatus;
  /** The status's word, in the store's copy: "LIVE". */
  label: string;
  /** `sm` in a queue row, `md` on a card or the stage. */
  size?: "sm" | "md";
  className?: string;
}

/**
 * A live break's broadcast state in a word: LIVE with a pulsing red dot, ENDING with a still amber
 * one, both on the neutral fill in the primary text; NEXT; or an outlined, quiet CLOSED. The word
 * carries the meaning; the dot repeats it.
 */
export function LiveBreakStatusBadge({
  status,
  label,
  size = "md",
  className,
}: LiveBreakStatusBadgeProps) {
  const dot = status === "live" || status === "ending" ? DOT[status] : null;
  return (
    <Badge
      tone={TONE[status]}
      className={cn(
        PILL[status],
        size === "sm" && "px-1.5 py-px",
        "shrink-0 whitespace-nowrap",
        className,
      )}
    >
      {dot && (
        <span
          aria-hidden
          data-status-dot={status}
          className={cn(
            "inline-block shrink-0 rounded-pill",
            size === "sm" ? "size-1.5" : "size-2",
            dot,
          )}
        />
      )}
      {label}
    </Badge>
  );
}
